/**
 * Render-time "Reviewed" stamp for Share card + PNG (complete hands).
 * Local datetime via Intl — no persistence; no win or solver implication.
 */
import { getLocale, type Locale } from '../i18n/strings'

const LOCALE_BCP47: Record<Locale, string> = {
  en: 'en',
  ko: 'ko-KR',
  ja: 'ja-JP',
  'pt-BR': 'pt-BR',
  es: 'es',
  ru: 'ru',
  de: 'de',
  fr: 'fr',
  'zh-TW': 'zh-TW',
}

/** Browser / app locale → BCP47 tag for Intl.DateTimeFormat. */
export function shareStampLocaleTag(locale?: Locale): string {
  const id = locale ?? (typeof getLocale === 'function' ? getLocale() : 'en')
  return LOCALE_BCP47[id] ?? 'en'
}

/**
 * Local datetime for the Reviewed stamp.
 * Prefers the browser timeZone (Asia/Seoul-friendly when that is the zone)
 * and formats with the active UI locale.
 */
export function formatShareReviewedAt(
  when: Date = new Date(),
  locale?: Locale,
): string {
  const tag = shareStampLocaleTag(locale)
  let timeZone: string | undefined
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    // ignore — formatter falls back to runtime default
  }
  try {
    return new Intl.DateTimeFormat(tag, {
      dateStyle: 'medium',
      timeStyle: 'short',
      ...(timeZone ? { timeZone } : {}),
    }).format(when)
  } catch {
    return when.toISOString()
  }
}

/** Optional label + datetime, e.g. "Reviewed · Sep 9, 2026, 12:26 PM". */
export function formatShareReviewedLine(
  label: string,
  when: Date = new Date(),
  locale?: Locale,
): string {
  const dt = formatShareReviewedAt(when, locale)
  const trimmed = label.trim()
  return trimmed ? `${trimmed} · ${dt}` : dt
}
